import { Button, Flex, Group, Stack } from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteModal } from '../../components/DeleteModal';
import { PageHeader } from '../../components/PageHeader';
import { Paper } from '../../components/Paper';
import { useModal } from '../../hooks/useModal';
import { useResponsiveSpace } from '../../hooks/useResponsiveSpace';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { mutate } from '../../util/api/mutate';
import { query } from '../../util/api/query';
import { BarcodesTable } from './components/BarcodesTable';
import { BranchStockTableBox } from './components/BranchStockTableBox';
import { EditProductModal } from './components/EditProductModal';
import { NewBarcodeButton } from './components/NewBarcodeButton';
import { convertProduct } from './components/ProductRow';
import {
	ProductGeneralInfo,
	ProductStockCost,
} from './components/SingleProductTiles';
import type { BarcodeDto } from './types/BarcodeDto';
import type { ProductDto } from './types/ProductDto';

export function SingleProduct() {
	const { skuid, organisation } = useParams();

	// biome-ignore lint/style/noNonNullAssertion: <explanation>
	const encodedSkuid = encodeURIComponent(skuid!);

	const navigate = useNavigate();

	const productQuery = query<ProductDto>(
		`${organisation}/product/skuid/${encodedSkuid}`,
	);

	const editProductModal = useModal();
	const deleteModal = useModal();

	const branchStocksHeight = useResponsiveSpace([
		[753, '15vh'],
		[1048, '40vh'],
	]);

	const barcodeTableHeight = useResponsiveSpace([
		[753, '14vh'],
		[1048, '11vh'],
	]);

	const product = useMemo(() => {
		return productQuery.data?.value;
	}, [productQuery]);

	const barcodesQuery = query<BarcodeDto[]>(
		`${organisation}/barcodes/all/${encodedSkuid}`,
	);

	const deleteProductMutation = mutate<ProductDto>(
		`${organisation}/product/delete/${skuid}`,
	);

	return (
		<>
			{product && (
				<>
					<DeleteModal
						isOpened={deleteModal.isOpen}
						close={deleteModal.close}
						titleType={'Product'}
						itemDescription={product.skuid}
						extraInfo={'All branch stocks & barcodes will also be removed.'}
						mutation={deleteProductMutation}
						onComplete={() => navigate(-1)}
					/>
					<EditProductModal
						close={editProductModal.close}
						isOpened={editProductModal.isOpen}
						updateProductRecord={() => {
							productQuery.refetch();
							productQuery.remove();
						}}
						product={convertProduct(product)}
					/>
				</>
			)}
			<PageHeader
				title={
					<>
						Product <i>{productQuery.data?.value.skuid ?? 'Undefined'}</i>
					</>
				}
				hideFiltering
			>
				<Group>
					<Button
						variant={'filled'}
						color={'medepos.1'}
						autoContrast
						px={'xs'}
						loading={productQuery.isLoading}
						onClick={() => editProductModal.open()}
					>
						<IconEdit {...defaultIconStyle} />
					</Button>
					<Button
						color={'medepos.1'}
						variant={'filled'}
						px={'xs'}
						onClick={() => deleteModal.open()}
						loading={productQuery.isLoading}
					>
						<IconTrash {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<Flex w={'100%'} h={'100%'} dir={'column'} gap={20}>
				<Stack w={'100%'}>
					<Paper title={'general info'} h={'100%'}>
						<ProductGeneralInfo
							product={product}
							isLoading={productQuery.isLoading}
						/>
					</Paper>
				</Stack>

				<Stack w={'100%'} justify={'space-between'}>
					<Paper title={'stock & cost'}>
						<ProductStockCost
							product={product}
							isLoading={productQuery.isLoading}
						/>
					</Paper>
					<Paper
						title={'barcodes'}
						w={'100%'}
						h={'100%'}
						headerContent={
							<NewBarcodeButton
								skuid={encodedSkuid}
								triggerListUpdate={() => {
									barcodesQuery.refetch();
									barcodesQuery.remove();
								}}
							/>
						}
					>
						<BarcodesTable
							h={barcodeTableHeight}
							barcodesQuery={barcodesQuery}
							skuid={encodedSkuid}
						/>
					</Paper>
				</Stack>
			</Flex>

			<BranchStockTableBox
				skuid={encodedSkuid}
				vatRateId={product?.vatRateId}
				h={branchStocksHeight}
			/>
		</>
	);
}
